'use client';

import { useState, useEffect, useCallback } from 'react';

/**
 * Online/offline detection driven purely by browser `online` / `offline` events.
 * Previously this hook polled `/api/health` every 30s per open tab — but that
 * route doesn't exist in the Next.js app, so every poll hit a 404 serverless
 * render on Vercel (2,880 invocations/day per tab). Socket.io already signals
 * real connectivity via its own heartbeat, so browser events are sufficient.
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(true);
  const [wasOffline, setWasOffline] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  const handleOnline = useCallback(() => {
    setIsOnline(true);
    setReconnecting(false);
  }, []);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setWasOffline(true);
    setReconnecting(false);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setIsOnline(navigator.onLine);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return { isOnline, wasOffline, reconnecting };
}
